<script>
import { CellGroup, Cell, Popup, Picker, List, Empty } from 'vant'
import ImageViewer from '@/components/org/ImageViewer'
import BottomToolbar from '@/components/org/BottomToolbar'
import { QNImageUtil } from '@/utils'

export default {
    //--------------------------------------------------
	// component name
	//--------------------------------------------------
	name: 'org-staff-detail',

    //--------------------------------------------------
	// component components
	//--------------------------------------------------
    components: {
        'van-cell-group': CellGroup,
        'van-cell': Cell,
        'van-popup': Popup,
        'van-picker': Picker,
        'van-list': List,
        'van-empty': Empty,
        'image-viewer': ImageViewer,
        'bottom-toolbar': BottomToolbar,
    },

    //--------------------------------------------------
    // component emits
    //--------------------------------------------------
	emits: ['custom-event'],

    //--------------------------------------------------
    // component data
    //--------------------------------------------------
    data() {
        return {
            staff: {
                id: null,
                organizationId: null,
                name: null,
                logoUrl: null,
                role: null,
                roleLabel: null,
                remark: null,
                isLogon: null,
                isSuperAdmin: null,
                matchList: null,
            },
            matchList: null,
            showPopup: null,
            isGrantMode: null,
            locale: {}
        }
    },

    //--------------------------------------------------
    // computed setter and getter
    //--------------------------------------------------
    computed: {
        isMatchOperator() {
            return (this.staff.role == '2')
        },
        matchOptions() {
            if (this.matchList == null) {
                return []
            }
            if (this.isGrantMode) {
                return this.matchList.filter(match => match.staffId == null)
            }
            else {
                return this.matchList.filter(match => match.staffId != null)
            }
        },
        staffMatchList() {
            if (this.matchList == null) {
                return []
            }
            return this.matchList.filter(match => match.staffId != null)
        },
        isStaffMatchListEmtpy() {
            if (this.staff.role == '2') {
                if (this.matchList == null || this.matchList.filter(match => match.staffId != null).length == 0) {
                    return true
                }
            }
            return false
        },
        operatorIconButtons() {
            console.log('operatorIconButtons', this.staff.isLogon, this.staff.role)
            const iconButtons = []
            if (!this.staff.isLogon) {
                iconButtons.push({ type: 'invite-staff', iconType: 'vant', icon: 'wechat', label: this.locale['invite'] })
            }
            if (this.staff.role == '2') {
                iconButtons.push({ type: 'grant-match', iconType: 'vant', icon: 'certificate', label: this.locale['match-grant'] })
                iconButtons.push({ type: 'revoke-match', iconType: 'vant', icon: 'revoke', label: this.locale['match-revoke'] })
            }
            iconButtons.push({ type: 'edit-staff', iconType: 'vant', icon: 'edit', label: this.locale['edit'] })
            return iconButtons
        },
    },

    //--------------------------------------------------
    // component methods
    //--------------------------------------------------
    methods: {
        onBottomToolbarIconClick(type) {
            if (type == 'invite-staff') {
                this.onInviteBtnClick()
            }
            else if (type == 'grant-match') {
                this.onGrantBtnClick()
            }
            else if (type == 'revoke-match') {
                this.onRevokeBtnClick()
            }
            else if (type == 'edit-staff') {
                this.onEditBtnClick()
            }
        },
        onInviteBtnClick() {
            console.log('onInviteBtnClick')
            this.$emit('custom-event', {
                type: 'invite',
                data: {
                    id: this.staff.id,
                    organizationId: this.staff.organizationId,
                    role: this.staff.role,
                    roleLabel: this.staff.roleLabel
                }
            })
        },
        onGrantBtnClick() {
            this.isGrantMode = true
            this.showPopup = true
        },
        onRevokeBtnClick() {
            this.isGrantMode = false
            this.showPopup = true
        },
        onEditBtnClick() {
            console.log('onEditBtnClick')
            this.$emit('custom-event', {
                type: 'navigate',
                data: {
                    name: 'OrgEditStaff',
                    params: { id: this.staff.id }
                }
            })
        },
        onMatchOptionPickerConfirm(data) {
            console.log('onMatchOptionPickerConfirm', this.isGrantMode, data)
            this.showPopup = false
            if (this.isGrantMode) {
                this.$emit('custom-event', {
                    type: 'grant',
                    data: {
                        matchId: data.matchId,
                        staffId: this.staff.id
                    }
                })
            }
            else {
                this.$emit('custom-event', {
                    type: 'revoke',
                    data: {
                        matchId: data.matchId,
                        staffId: this.staff.id
                    }
                })
            }
        },
        /**
         * @private
         * Adjust the image url according to the width and height.
         * @param img The identifier string for the image source.
         * @param w The image width number.
         * @param h The image height number.
         * @return The completed full url string.
         */
		adjustImageSize(img, w, h) {
            // console.log('adjustImageSize', img, w, h)
            if (img) {
                return QNImageUtil.imageView(img, w, h)
            }
			else {
                return null
            }
		},
    },
}
</script>

<template>
    <div class="staff-detail">
        <van-cell-group>
            <van-cell :title="locale['staff-name-label']" :value="staff.name"/>
            <van-cell :title="locale['staff-role-label']" :value="staff.roleLabel"/>
            <image-viewer
                :label="locale['staff-logo-label']"
                :imageUrl="adjustImageSize(staff.logoUrl, 160, 160)"
                :previewImageUrl="adjustImageSize(staff.logoUrl, 750, 750)"
            />
            <van-cell :title="locale['staff-remark']" :value="staff.remark"/>
        </van-cell-group>
        <div v-if="isMatchOperator" class="minor-text u-outer-padding t-outer-margin">
            <span>{{ locale['match-permission'] }}</span>
        </div>
        <div v-if="!isStaffMatchListEmtpy">
            <van-list>
                <van-cell 
                    v-for="item in staffMatchList" 
                    :key="item.matchId" 
                    :title="item.matchName"
                />
            </van-list>
        </div>
        <div v-else @click="onGrantBtnClick">
            <van-empty 
                :description="locale['staff-match-empty-hint']"
                image="search"
            />
        </div>
        <bottom-toolbar
            type="icons"
            :mainIcons="operatorIconButtons"
            @click-main-icon="onBottomToolbarIconClick"
        />
        <van-popup v-model="showPopup" position="bottom">
            <van-picker
                :columns="matchOptions"
                value-key="matchName"
                show-toolbar
                @cancel="showPopup = false"
                @confirm="onMatchOptionPickerConfirm"
            />
        </van-popup>
    </div>
</template>